import {Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {AudioResultComponent} from "../exam-part-results/audio-result/audio-result.component";
import {LoadExamPartResultDirective} from "../../directives/load-exam-part-result.directive";
import {CorResultComponent} from "../exam-part-results/cor-result/cor-result.component";
import {DictResultComponent} from "../exam-part-results/dict-result/dict-result.component";
import {FillMultiResultComponent} from "../exam-part-results/fill-multi-result/fill-multi-result.component";
import {PicturesResultComponent} from "../exam-part-results/pictures-result/pictures-result.component";
import {JumbleResultComponent} from "../exam-part-results/jumble-result/jumble-result.component";
import {V3ExamSessionPart, V3ExerciseSessionQuestion} from "../../model/v3/cspa";
import {CspaRestServiceV3} from "../../services/rest/v3/cspa-rest.service";
import {Subscription} from "rxjs";

@Component({
    selector: 'app-exam-part-result',
    templateUrl: './exam-part-result.component.html',
    styleUrls: ['./exam-part-result.component.scss'],
    standalone: false
})
export class ExamPartResultComponent implements OnInit, OnDestroy {
  @ViewChild(LoadExamPartResultDirective, {static: true}) loadExamPartResultDirective!: LoadExamPartResultDirective

  private _examPart: V3ExamSessionPart
  private _examPartTypes: string[] = []
  private _questions: V3ExerciseSessionQuestion[] = []
  private _lastLoadKey: string | null = null
  private questionsSub?: Subscription
  private _personId!: number
  private _examSessionUuid!: string
  loadingQuestions = false

  @Input() set personId(value: number) {
    this._personId = value
    this.tryLoadQuestions()
  }

  get personId(): number {
    return this._personId
  }

  @Input() set examSessionUuid(value: string) {
    this._examSessionUuid = value
    this.tryLoadQuestions()
  }

  get examSessionUuid(): string {
    return this._examSessionUuid
  }

  @Input() set examPart(part: V3ExamSessionPart) {
    if (!part) return
    this._examPart = part
    this.tryLoadQuestions()
  }

  get examPart(): V3ExamSessionPart {
    return this._examPart
  }

  constructor(
    private cspaService: CspaRestServiceV3
  ) {
  }

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    this.questionsSub?.unsubscribe()
  }

  private tryLoadQuestions() {
    if (!this._examPart || !this.personId || !this.examSessionUuid) {
      return
    }
    const loadKey = `${this.personId}:${this.examSessionUuid}:${this._examPart.num}`
    if (this._lastLoadKey === loadKey) {
      return
    }
    this._lastLoadKey = loadKey
    this.questionsSub?.unsubscribe()
    this.loadingQuestions = true
    this.questionsSub = this.cspaService.getPartQuestions(this.personId, this.examSessionUuid, this._examPart.num)
      .subscribe(questions => {
        this._questions = questions
        this._examPartTypes = Array.from(new Set(questions.map(p => p.questionDef?.['@type'])))
        this.onLoadQuestion()
        this.loadingQuestions = false
      }, _ => {
        this._questions = []
        this._examPartTypes = []
        this.onLoadQuestion()
        this.loadingQuestions = false
      })
  }

  onLoadQuestion() {
    this.loadExamPartResultDirective.viewContainerRef.clear();
    this._examPartTypes.forEach(type => {
      let componentRef: any;
      const normalizedType = this.normalizeQuestionType(type)
      if (normalizedType === 'correction') {
        componentRef = this.loadExamPartResultDirective.viewContainerRef.createComponent(CorResultComponent)
      } else if (normalizedType === 'dictation') {
        componentRef = this.loadExamPartResultDirective.viewContainerRef.createComponent(DictResultComponent)
      } else if (normalizedType === 'fillMulti') {
        componentRef = this.loadExamPartResultDirective.viewContainerRef.createComponent(FillMultiResultComponent)
      } else if (normalizedType === 'images') {
        componentRef = this.loadExamPartResultDirective.viewContainerRef.createComponent(PicturesResultComponent)
      } else if (normalizedType === 'jumble') {
        componentRef = this.loadExamPartResultDirective.viewContainerRef.createComponent(JumbleResultComponent)
      } else if (normalizedType === 'audio') {
        componentRef = this.loadExamPartResultDirective.viewContainerRef.createComponent(AudioResultComponent)
      }
      if (componentRef) {
        componentRef.instance.questions = this._questions.filter(q => q.questionDef['@type'] == type)
      }
    })
  }

  private normalizeQuestionType(type: string): string {
    switch (type) {
      case 'cor':
      case 'V3Correction':
      case 'correction':
        return 'correction'
      case 'dict':
      case 'V3Dictation':
      case 'dictation':
        return 'dictation'
      case 'ef':
      case 'V3FillMulti':
      case 'fillMulti':
      case 'fill-multi':
        return 'fillMulti'
      case 'pct':
      case 'img':
      case 'images':
      case 'V3Images':
        return 'images'
      case 'jmb':
      case 'V3Jumble':
      case 'jumble':
        return 'jumble'
      case 'audio':
      case 'V3Audio':
        return 'audio'
      default:
        return type
    }
  }

}
